import { useEffect } from "react";
import { TOKEN_CONFIG } from "@/features/auth/config/keycloak";
import {
  introspectToken,
  performLogout,
  refreshAccessTokens,
} from "@/features/auth/services/keycloakAuthService";
import { env } from "@/shared/constants/env";
import { onForeground } from "@/shared/platform/appState";
import { useSessionStore } from "@/shared/stores/sessionStore";

// Surveillance du cycle de vie des tokens — extraite de RootNavigator.tsx.
//
// En natif, cette logique vivait dans le composant de navigation parce qu'il
// était le seul point monté en permanence. Sur web, la router est déclarative :
// on en fait un hook, monté par le shell d'authentification.

/**
 * Vérifie et rafraîchit les tokens. Défini hors du composant pour éviter les
 * re-créations à chaque rendu. Lit le store Zustand directement (API non
 * réactive, sûre en dehors des hooks).
 */
async function checkAndRefreshTokens(): Promise<void> {
  const state = useSessionStore.getState();
  const { accessToken, refreshToken, tokenExpiry, refreshTokenExpiry, updateTokens } = state;

  if (!accessToken || !refreshToken) return;

  const now = Date.now();

  // ── 1. Refresh token expiré → logout forcé immédiat ───────────────────────
  // refreshTokenExpiry === 0 = offline session Keycloak (expiry gérée serveur).
  if (refreshTokenExpiry && refreshTokenExpiry > 0) {
    if (now >= refreshTokenExpiry - TOKEN_CONFIG.REFRESH_TOKEN_BUFFER) {
      if (env.isDev) console.log("[TokenWatch] Refresh token expiré → logout");
      performLogout();
      return;
    }
  }

  // ── 2. Access token proche de l'expiry → refresh proactif ─────────────────
  if (tokenExpiry && now >= tokenExpiry - TOKEN_CONFIG.PREEMPTIVE_REFRESH) {
    try {
      if (env.isDev) console.log("[TokenWatch] Refresh proactif…");
      const tokens = await refreshAccessTokens(refreshToken);
      updateTokens(
        tokens.access_token,
        tokens.refresh_token,
        now + tokens.expires_in * 1000,
        tokens.refresh_expires_in > 0 ? now + tokens.refresh_expires_in * 1000 : 0,
      );
      if (env.isDev) console.log("[TokenWatch] Refresh proactif réussi");
    } catch (e) {
      // Échec réseau ou token révoqué côté serveur.
      if (env.isDev) console.warn("[TokenWatch] Refresh proactif échoué :", e);
      // On ne logout pas ici — l'intercepteur Axios gérera le prochain 401.
      // Exception : si le refresh token est définitivement invalide.
      if (e instanceof Error && e.message.includes("non-recoverable")) {
        performLogout();
      }
    }
  }
}

/**
 * Surveille les tokens tant qu'une session existe.
 *
 * Déclenché :
 *   • immédiatement au login (ou au chargement si session persistée)
 *   • toutes les WATCH_INTERVAL secondes
 *   • à chaque retour de l'onglet au premier plan
 *
 * ## Ce que le web change
 * `AppState.addEventListener("change")` devient `onForeground` (visibilitychange
 * + focus, cf. shared/platform/appState). La différence pratique est en NOTRE
 * faveur : un onglet en arrière-plan voit son `setInterval` fortement ralenti
 * par le navigateur (jusqu'à une fois par minute, voire suspendu), ce qui rend
 * la vérification au retour au premier plan encore plus nécessaire qu'en natif.
 * Elle est déjà là, donc rien à ajouter — mais il ne faut pas la retirer en
 * croyant que l'intervalle suffit.
 */
export function useTokenWatcher(): void {
  const accessToken = useSessionStore((s) => s.accessToken);

  useEffect(() => {
    if (!accessToken) return;

    // Vérification immédiate (couvre le chargement avec session persistée).
    void checkAndRefreshTokens();

    const interval = setInterval(checkAndRefreshTokens, TOKEN_CONFIG.WATCH_INTERVAL);
    const unsubscribe = onForeground(() => void checkAndRefreshTokens());

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [accessToken]); // redémarre si on se connecte avec un autre compte
}

/**
 * Validation SERVEUR du token persisté au boot (POST /auth/introspect).
 * Valide le token côté Gateway/Keycloak et détecte une révocation — ce que le
 * simple contrôle d'`exp` local ne voit pas.
 *
 * ⚠️ Best-effort POUR L'INSTANT : le Gateway renvoie `inactive_keycloak_token`
 * sur des tokens pourtant valides (bug d'introspection backend — client
 * d'introspection mal configuré). On NE DÉCONNECTE donc PAS sur `active: false`,
 * pour ne pas éjecter un utilisateur fraîchement connecté. Dès que
 * /auth/introspect est fiable, activer l'enforcement ci-dessous. La révocation
 * reste de toute façon couverte par l'intercepteur Axios (401 → refresh →
 * logout si échec).
 */
export function useBootIntrospection(): void {
  const accessToken = useSessionStore((s) => s.accessToken);
  const tokensHydrated = useSessionStore((s) => s._tokensHydrated);

  useEffect(() => {
    if (!accessToken || !tokensHydrated) return;
    introspectToken(accessToken)
      .then((res) => {
        if (env.isDev) {
          console.log("[Boot] introspect:", { active: res.active, sub: res.sub, exp: res.exp });
        }
        // TODO(backend): activer une fois /auth/introspect fiable :
        // if (!res.active) performLogout();
      })
      .catch((e) => {
        if (env.isDev) console.warn("[Boot] introspect failed (best-effort):", e);
      });
  }, [accessToken, tokensHydrated]);
}
